/**
 * Human-facing reference numbers.
 *
 * These appear in SMS messages, on bank transfer narrations and in
 * conversations between officers and members, so they are short, unambiguous
 * and free of characters that get misread when handwritten or dictated.
 */
/** `UNF-000123` — the loan reference used throughout the system (§19). */
export function formatLoanNumber(sequence, prefix = 'UNF') {
    if (!Number.isInteger(sequence) || sequence < 1) {
        throw new Error(`Loan sequence must be a positive integer, received ${sequence}.`);
    }
    return `${prefix}-${sequence.toString().padStart(6, '0')}`;
}
/** `APP-2026-000123` — application references carry the year for filing. */
export function formatApplicationNumber(sequence, year, prefix = 'APP') {
    return `${prefix}-${year}-${sequence.toString().padStart(6, '0')}`;
}
/** `RPY-000123` — repayment receipt reference. */
export function formatRepaymentReference(sequence, prefix = 'RPY') {
    return `${prefix}-${sequence.toString().padStart(6, '0')}`;
}
/** `DSB-000123` — disbursement reference. */
export function formatDisbursementReference(sequence, prefix = 'DSB') {
    return `${prefix}-${sequence.toString().padStart(6, '0')}`;
}
const LOAN_NUMBER_PATTERN = /^([A-Z]{2,5})-(\d{6,})$/;
export function parseLoanNumber(value) {
    const match = LOAN_NUMBER_PATTERN.exec(value.trim().toUpperCase());
    if (!match)
        return null;
    return { prefix: match[1], sequence: Number(match[2]) };
}
/**
 * Normalises a Nigerian phone number to E.164 (`+2349168484980`).
 *
 * Members type these in every conceivable format, and SMS/WhatsApp providers
 * reject anything that is not E.164 — so normalise once, on the way in.
 */
export function normaliseNigerianPhone(input) {
    const digits = input.replace(/[^\d+]/g, '');
    // +234XXXXXXXXXX
    if (/^\+234[789]\d{9}$/.test(digits))
        return digits;
    // 234XXXXXXXXXX
    if (/^234[789]\d{9}$/.test(digits))
        return `+${digits}`;
    // 0XXXXXXXXXX (local format)
    if (/^0[789]\d{9}$/.test(digits))
        return `+234${digits.slice(1)}`;
    // XXXXXXXXXX (missing leading zero)
    if (/^[789]\d{9}$/.test(digits))
        return `+234${digits}`;
    return null;
}
/** `(+234) 916 848 4980` — the display format used on the scholarship site. */
export function formatNigerianPhone(e164) {
    const match = /^\+234(\d{3})(\d{3})(\d{4})$/.exec(e164);
    if (!match)
        return e164;
    return `(+234) ${match[1]} ${match[2]} ${match[3]}`;
}
/** Full name from parts, skipping absent middle names without leaving a double space. */
export function formatFullName(parts) {
    return [parts.firstName, parts.middleName, parts.lastName]
        .filter((part) => Boolean(part && part.trim()))
        .join(' ');
}
/** `JD` — avatar initials. */
export function initials(parts) {
    return `${parts.firstName.charAt(0)}${parts.lastName.charAt(0)}`.toUpperCase();
}
//# sourceMappingURL=references.js.map